import type { AcceptTaskStatus, AgentRunDetail } from '@fos/shared/contracts/agents';
import { ACCEPT_TASK_STATUS_LABELS, AGENT_DEPLOY_SEPARATE_NOTE } from '@fos/shared/labels';
import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import {
  Button,
  Checkbox,
  ContextBanner,
  Dialog,
  Field,
  FormMessage,
  Input,
  Segmented,
  Skeleton,
  Textarea,
  toast,
} from '../../../ui';

/**
 * Dialogi przebiegu (karta zadania i szczegóły przebiegu): „Odbierz wynik” (status zadania + notatka do Wiedzy
 * z propozycji), „Poproś o wdrożenie” (osobna akceptacja), „Anuluj przebieg” (bez cofania wykonanych kroków).
 * Uprawnienia sprawdza backend; UI tylko ukrywa niedostępne opcje.
 */

type RunRef = Pick<AgentRunDetail, 'id' | 'number' | 'title' | 'task'>;

/** Odświeżenie danych po zmianie runu (Agenci, karta zadania, Akceptacje). */
export function useRunInvalidate() {
  const utils = trpc.useUtils();
  return async () => {
    await Promise.all([utils.agents.invalidate(), utils.tasks.invalidate(), utils.approvals.invalidate()]);
  };
}

type TaskChoice = 'keep' | AcceptTaskStatus;

export function AcceptRunDialog({
  run,
  open,
  onOpenChange,
  onAccepted,
}: {
  run: RunRef;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccepted?: (detail: AgentRunDetail) => void;
}) {
  const access = useAccess();
  const invalidate = useRunInvalidate();
  const canTask = Boolean(run.task) && access.can('tasks', 'edit');
  const canKnowledge = access.can('knowledge', 'edit');
  const proposal = trpc.agents.knowledgeProposal.useQuery({ id: run.id }, { enabled: open && canKnowledge });
  const [taskStatus, setTaskStatus] = useState<TaskChoice>('review');
  const [addNote, setAddNote] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTaskStatus(canTask ? 'review' : 'keep');
    setError(null);
  }, [open, canTask]);

  useEffect(() => {
    if (!proposal.data) return;
    setTitle(proposal.data.title);
    setBody(proposal.data.bodyMd);
    setAddNote(proposal.data.scope === 'project');
  }, [proposal.data]);

  const accept = trpc.agents.accept.useMutation({
    onSuccess: async (detail) => {
      toast.success('Wynik odebrany', { description: AGENT_DEPLOY_SEPARATE_NOTE });
      await invalidate();
      onOpenChange(false);
      onAccepted?.(detail);
    },
    onError: (err) => setError(validationIssues(err)[0]?.message ?? errorMessage(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    accept.mutate({
      id: run.id,
      ...(canTask && taskStatus !== 'keep' ? { taskStatus } : {}),
      ...(canKnowledge && addNote && proposal.data ? { knowledgeNote: { title: title.trim(), bodyMd: body } } : {}),
    });
  };

  const noteTarget = proposal.data?.projectName ? `projektu „${proposal.data.projectName}”` : 'klienta';

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Odbierz wynik"
      description={`Przebieg #${run.number}: ${run.title}. Odbiór potwierdza, że wynik jest w porządku — nic nie wdraża.`}
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        {canTask && (
          <Field
            label="Status zadania"
            hint={
              taskStatus === 'done'
                ? 'Po odebraniu zadanie zostanie zamknięte (Gotowe).'
                : taskStatus === 'review'
                  ? 'Po odebraniu zadanie trafi do kolumny „Do odbioru”.'
                  : 'Po odebraniu status zadania się nie zmieni.'
            }
          >
            <Segmented
              aria-label="Status zadania po odbiorze"
              value={taskStatus}
              onValueChange={setTaskStatus}
              options={[
                { value: 'keep', label: 'Bez zmiany' },
                { value: 'review', label: ACCEPT_TASK_STATUS_LABELS.review },
                { value: 'done', label: ACCEPT_TASK_STATUS_LABELS.done },
              ]}
            />
          </Field>
        )}
        {canKnowledge && (
          <div className="flex flex-col gap-3">
            {proposal.isLoading ? (
              <Skeleton className="h-5 w-64" />
            ) : proposal.data ? (
              <Checkbox
                checked={addNote}
                onCheckedChange={setAddNote}
                label={`Dodaj notatkę techniczną do Wiedzy ${noteTarget} (z linkiem do przebiegu)`}
              />
            ) : null}
            {addNote && proposal.data && (
              <>
                <Field label="Tytuł notatki">
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} required />
                </Field>
                <Field label="Treść" hint="Markdown. Możesz ją poprawić przed zapisaniem.">
                  <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} />
                </Field>
              </>
            )}
          </div>
        )}
        <ContextBanner tone="info">{AGENT_DEPLOY_SEPARATE_NOTE}</ContextBanner>
        {error && <FormMessage>{error}</FormMessage>}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" loading={accept.isPending}>
            Odbierz wynik
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function RequestDeployDialog({
  run,
  open,
  onOpenChange,
}: {
  run: RunRef;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const invalidate = useRunInvalidate();
  const [environment, setEnvironment] = useState('produkcja');
  const [site, setSite] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const request = trpc.agents.requestDeploy.useMutation({
    onSuccess: async ({ approvalId }) => {
      toast.success('Prośba o wdrożenie czeka w Akceptacjach', {
        description: 'Nic nie zostanie wdrożone bez decyzji osoby z uprawnieniem.',
        action: { label: 'Pokaż', onClick: () => navigate(`/akceptacje?id=${approvalId}`) },
      });
      await invalidate();
      onOpenChange(false);
    },
    onError: (err) => setError(validationIssues(err)[0]?.message ?? errorMessage(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    request.mutate({
      id: run.id,
      environment: environment.trim() || 'produkcja',
      ...(site.trim() ? { site: site.trim() } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Poproś o wdrożenie"
      description="Wdrożenie to osobna decyzja: trafi do Akceptacji i wykona się dopiero po zgodzie osoby z uprawnieniem „Wdraża na produkcję”."
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Środowisko">
            <Input value={environment} onChange={(e) => setEnvironment(e.target.value)} maxLength={60} />
          </Field>
          <Field label="Strona" hint="Opcjonalnie, np. rea.pl">
            <Input value={site} onChange={(e) => setSite(e.target.value)} maxLength={200} />
          </Field>
        </div>
        <Field label="Opis dla zatwierdzającego" hint="Domyślnie podsumowanie wyniku przebiegu.">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000} />
        </Field>
        {error && <FormMessage>{error}</FormMessage>}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" loading={request.isPending}>
            Poproś o wdrożenie
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function CancelRunDialog({
  run,
  open,
  onOpenChange,
}: {
  run: RunRef;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invalidate = useRunInvalidate();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cancel = trpc.agents.cancel.useMutation({
    onSuccess: async (detail) => {
      toast.success(detail.status === 'cancelled' ? 'Przebieg anulowany' : 'Poproszono runner o przerwanie pracy');
      await invalidate();
      onOpenChange(false);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="s"
      title="Anulować przebieg?"
      description="Anulowanie zatrzyma dalsze kroki, ale nie cofnie tych, które już się wykonały (np. utworzonego brancha)."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Wróć
          </Button>
          <Button
            variant="primary"
            loading={cancel.isPending}
            onClick={() => cancel.mutate({ id: run.id, ...(reason.trim() ? { reason: reason.trim() } : {}) })}
          >
            Anuluj przebieg
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Powód" hint="Opcjonalnie — trafi do dziennika przebiegu.">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
        </Field>
        {error && <FormMessage>{error}</FormMessage>}
      </div>
    </Dialog>
  );
}
